import React from 'react';

import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { QUESTION_TEXT_SIZES, QuestionPrompt, QuestionText, RevealCardText } from 'utils/questionMarkdown';
import { ANSWER_REFERENCE_PREVIEW, AnswerReferenceContext } from 'utils/answerReferences';
import {
  QUESTION_CUSTOM_TEXT_SIZE_MIN,
  QUESTION_CUSTOM_TEXT_SIZE_MAX,
  isQuestionTextSize,
} from 'utils/questionMarkdownSizes';

type Props = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  markdown: boolean;
  previewRole: 'prompt' | 'body' | 'compact' | 'reveal-front' | 'reveal-back';
  citations?: string[];
  rows?: number;
  disabled?: boolean;
  /** A containing editor supplies the combined learner preview. */
  parentPreview?: boolean;
  placeholder?: string;
  invalid?: boolean;
  describedBy?: string;
  /** Learner-visible header, text and subtext can show an earlier answer (REQ-106). */
  answerReferences?: boolean;
};

type Edit = { text: string; selectionStart: number; selectionEnd: number };
type Mode = 'edit' | 'split' | 'preview';

/** Source stays the canonical value: preview never rewrites an author's Markdown. */
export const QuestionMarkdownEditor: React.FC<Props> = ({
  id,
  label,
  value,
  onChange,
  markdown,
  previewRole,
  citations = [],
  rows = 3,
  disabled = false,
  parentPreview = false,
  placeholder,
  invalid,
  describedBy,
  answerReferences = false,
}) => {
  const inline = previewRole === 'compact' || previewRole === 'reveal-front';
  const reveal = previewRole === 'reveal-front' || previewRole === 'reveal-back';
  const textarea = React.useRef<HTMLTextAreaElement>(null);
  const pendingSelection = React.useRef<[number, number] | null>(null);
  const [mode, setMode] = React.useState<Mode>('split');
  const visibleMode = parentPreview ? 'edit' : mode;
  const [customSize, setCustomSize] = React.useState('');
  const [showCustomSizeError, setShowCustomSizeError] = React.useState(false);
  const customSizeToken = `${customSize}px`;
  const customSizeValid = isQuestionTextSize(customSizeToken);
  const customSizeError = showCustomSizeError && customSize && !customSizeValid
    ? `Enter a whole number from ${QUESTION_CUSTOM_TEXT_SIZE_MIN} to ${QUESTION_CUSTOM_TEXT_SIZE_MAX}.`
    : null;

  React.useLayoutEffect(() => {
    if (pendingSelection.current && textarea.current) {
      textarea.current.focus();
      textarea.current.setSelectionRange(...pendingSelection.current);
      pendingSelection.current = null;
    }
  }, [value, mode]);

  const edit = (create: (selected: string) => Edit, block = false, range?: [number, number]) => {
    if (disabled) return;
    const start = range?.[0] ?? textarea.current?.selectionStart ?? value.length;
    const end = range?.[1] ?? textarea.current?.selectionEnd ?? start;
    const change = create(value.slice(start, end));
    const before = value.slice(0, start);
    const after = value.slice(end);
    const prefix = block && before && !before.endsWith('\n\n')
      ? (before.endsWith('\n') ? '\n' : '\n\n') : '';
    const suffix = block && after && !after.startsWith('\n\n')
      ? (after.startsWith('\n') ? '\n' : '\n\n') : '';
    pendingSelection.current = [
      start + prefix.length + change.selectionStart,
      start + prefix.length + change.selectionEnd,
    ];
    onChange(`${before}${prefix}${change.text}${suffix}${after}`);
  };

  const wrap = (marker: string, placeholder: string) => edit((selection) => {
    const content = selection || placeholder;
    return {
      text: `${marker}${content}${marker}`,
      selectionStart: marker.length,
      selectionEnd: marker.length + content.length,
    };
  });

  const block = (prefix: string, placeholder: string, everyLine = false) => edit((selection) => {
    const content = selection || placeholder;
    const text = everyLine
      ? content.split('\n').map((line) => `${prefix}${line}`).join('\n')
      : `${prefix}${content}`;
    return { text, selectionStart: prefix.length, selectionEnd: text.length };
  }, true);

  const insert = (text: string, isBlock = false) => edit(() => ({
    text,
    selectionStart: text.length,
    selectionEnd: text.length,
  }), isBlock);

  const applySize = (name: string, wholeBlock = false) => {
    if (!isQuestionTextSize(name)) return;
    let start = textarea.current?.selectionStart ?? value.length;
    let end = textarea.current?.selectionEnd ?? start;
    if (wholeBlock && start === end) {
      // Without a selection, the block control sizes the paragraph around the cursor.
      const previousBreak = value.slice(0, start).lastIndexOf('\n\n');
      const nextBreak = value.indexOf('\n\n', end);
      start = previousBreak < 0 ? 0 : previousBreak + 2;
      end = nextBreak < 0 ? value.length : nextBreak;
    }
    const asBlock = !inline && (wholeBlock || value.slice(start, end).includes('\n'));
    edit((selection) => {
      const content = selection || (asBlock ? 'Sized block' : 'Sized text');
      if (inline && /\n[ \t]*\n/.test(content)) {
        // Compact fields cannot contain block wrappers; retain paragraph breaks as separate spans.
        const text = content.split(/(\n[ \t]*\n+)/).map((part) =>
          part.trim() ? `[${part}]{.text-${name}}` : part,
        ).join('');
        return { text, selectionStart: text.indexOf('[') + 1, selectionEnd: text.lastIndexOf(']{') };
      }
      const opening = asBlock ? `::: {.text-${name}}\n` : '[';
      const closing = asBlock ? '\n:::' : `]{.text-${name}}`;
      return {
        text: `${opening}${content}${closing}`,
        selectionStart: opening.length,
        selectionEnd: opening.length + content.length,
      };
    }, asBlock, [start, end]);
  };

  const sizeMenu = (wholeBlock: boolean) => (
    <select
      className="min-h-8 max-w-full rounded-md border bg-background px-2 text-xs"
      aria-label={`${label} ${wholeBlock ? 'block size' : 'text size'}`}
      value=""
      disabled={disabled}
      onChange={(event) => applySize(event.target.value, wholeBlock)}
    >
      <option value="">{wholeBlock ? 'Block size' : 'Text size'}</option>
      {QUESTION_TEXT_SIZES.map(({ name, size }) => (
        <option key={name} value={name}>{name[0].toUpperCase() + name.slice(1)} ({size}px)</option>
      ))}
    </select>
  );

  const toolbarButton = (name: string, action: () => void, unavailable = false) => (
    <Button
      key={name}
      type="button"
      size="auto"
      variant="ghost"
      className="min-h-8 rounded-md px-2 py-1 text-xs"
      disabled={disabled || unavailable}
      onMouseDown={(event) => event.preventDefault()}
      onClick={action}
    >
      {name}
    </Button>
  );

  const source = (
    <Textarea
      ref={textarea}
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      rows={rows}
      disabled={disabled}
      placeholder={placeholder}
      className={markdown ? 'font-mono text-sm' : undefined}
      aria-invalid={invalid || undefined}
      aria-describedby={[describedBy, markdown ? `${id}-format-help` : ''].filter(Boolean).join(' ') || undefined}
      onKeyDown={(event) => {
        if (!markdown || !(event.ctrlKey || event.metaKey) || event.altKey) return;
        if (event.key.toLowerCase() === 'b') {
          event.preventDefault();
          wrap('**', 'bold text');
        } else if (event.key.toLowerCase() === 'i') {
          event.preventDefault();
          wrap('*', 'italic text');
        }
      }}
    />
  );

  if (!markdown) return source;

  return (
    <div className="min-w-0 space-y-2">
      {!parentPreview ? (
        <div className="flex flex-wrap gap-1" role="group" aria-label={`${label} editor view`}>
          {(['edit', 'split', 'preview'] as const).map((view) => (
            <Button
              key={view}
              type="button"
              size="auto"
              variant={mode === view ? 'secondary' : 'ghost'}
              className="min-h-8 rounded-md px-3 py-1 text-xs"
              aria-pressed={mode === view}
              onClick={() => setMode(view)}
            >
              {view === 'edit' ? 'Edit' : view === 'split' ? 'Edit + preview' : 'Preview'}
            </Button>
          ))}
        </div>
      ) : null}
      {visibleMode !== 'preview' ? (
        <div className="flex flex-wrap items-center gap-0.5 rounded-lg border p-1" role="group" aria-label={`${label} formatting`}>
          {toolbarButton('Bold', () => wrap('**', 'bold text'))}
          {toolbarButton('Italic', () => wrap('*', 'italic text'))}
          {toolbarButton('Strikethrough', () => wrap('~~', 'previous thought'))}
          {!reveal ? toolbarButton('Link', () => edit((selection) => {
            const content = selection || 'link text';
            return {
              text: `[${content}](https://example.com)`,
              selectionStart: content.length + 3,
              selectionEnd: content.length + 22,
            };
          })) : null}
          {sizeMenu(false)}
          {!inline ? (
            <>
              <select
                className="min-h-8 max-w-full rounded-md border bg-background px-2 text-xs"
                aria-label={`${label} heading`}
                value=""
                disabled={disabled}
                onChange={(event) => {
                  const heading = QUESTION_TEXT_SIZES.find(({ headingLevel }) => String(headingLevel) === event.target.value);
                  if (heading) block(`${'#'.repeat(heading.headingLevel)} `, `Heading ${heading.headingLevel}`);
                }}
              >
                <option value="">Heading</option>
                {[...QUESTION_TEXT_SIZES].reverse().map(({ headingLevel, size }) => (
                  <option key={headingLevel} value={headingLevel}>Heading {headingLevel} ({size}px)</option>
                ))}
              </select>
              {sizeMenu(true)}
              {toolbarButton('Bullets', () => block('- ', 'First item\nSecond item', true))}
              {toolbarButton('Numbered list', () => block('1. ', 'First step\nSecond step', true))}
              {toolbarButton('Nested list', () => insert('- Main point\n  - Supporting point', true))}
              {toolbarButton('Quote', () => block('> ', 'Quoted text', true))}
              {toolbarButton('Divider', () => insert('---', true))}
              {toolbarButton('Table', () => insert('| Situation | Response |\n| --- | --- |\n| Example | Your response |', true))}
            </>
          ) : null}
          {answerReferences ? toolbarButton('Earlier answer', () => edit((selection) => {
            const key = selection.trim() && !/[{}|\s]/.test(selection.trim()) ? selection.trim() : 'question_key';
            return { text: `{{${key}}}`, selectionStart: 2, selectionEnd: 2 + key.length };
          })) : null}
          {!reveal ? <select
            className="min-h-8 max-w-full rounded-md border bg-background px-2 text-xs"
            aria-label={`${label} insert citation`}
            value=""
            disabled={disabled || citations.length === 0}
            onChange={(event) => {
              if (event.target.value) insert(`{${event.target.value}}`);
            }}
          >
            <option value="">Insert citation</option>
            {citations.map((_, index) => (
              <option key={index} value={index + 1}>Citation {index + 1}</option>
            ))}
          </select> : null}
        </div>
      ) : null}
      {visibleMode !== 'preview' ? (
        <div role="group" aria-label={`${label} custom size`} className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor={`${id}-custom-size`} className="text-xs font-medium">Custom px</label>
            <Input
              id={`${id}-custom-size`}
              aria-label={`${label} custom px`}
              type="text"
              inputMode="numeric"
              value={customSize}
              onChange={(event) => {
                setCustomSize(event.target.value);
                setShowCustomSizeError(false);
              }}
              onFocus={() => setShowCustomSizeError(false)}
              onBlur={() => setShowCustomSizeError(true)}
              placeholder={`${QUESTION_CUSTOM_TEXT_SIZE_MIN}–${QUESTION_CUSTOM_TEXT_SIZE_MAX}`}
              disabled={disabled}
              aria-invalid={customSizeError ? true : undefined}
              aria-describedby={`${id}-custom-size-help${customSizeError ? ` ${id}-custom-size-error` : ''}`}
              className="h-8 w-24 rounded-md px-2 py-1 text-xs"
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (customSizeValid) applySize(customSizeToken);
                  else setShowCustomSizeError(true);
                }
              }}
            />
            {toolbarButton('Apply to text', () => applySize(customSizeToken), !customSizeValid)}
            {!inline ? toolbarButton('Apply to block', () => applySize(customSizeToken, true), !customSizeValid) : null}
          </div>
          <p id={`${id}-custom-size-help`} className="text-xs text-muted-foreground">
            Optional: {QUESTION_CUSTOM_TEXT_SIZE_MIN}–{QUESTION_CUSTOM_TEXT_SIZE_MAX} whole pixels at the browser’s default 16px text size; scales with reader preferences.
          </p>
          {customSizeError ? <p id={`${id}-custom-size-error`} role="alert" className="text-xs text-destructive">{customSizeError}</p> : null}
        </div>
      ) : null}
      <div className={visibleMode === 'split' && !inline ? 'grid min-w-0 gap-3 lg:grid-cols-2' : 'space-y-2'}>
        {visibleMode !== 'preview' ? source : null}
        {visibleMode !== 'edit' ? (
          <AnswerReferenceContext.Provider value={ANSWER_REFERENCE_PREVIEW}>
          <div role="region" aria-label={`${label} formatting preview`} className={`min-w-0 overflow-x-auto rounded-xl border bg-white p-4 ${inline ? 'text-sm' : 'text-base'}`}>
            {value ? (
              previewRole === 'reveal-front'
                ? <h3 className="font-headings-h3 font-[number:var(--headings-h3-font-weight)] text-foundation-base-darkbase-dark-500 text-[length:var(--headings-h3-font-size)] tracking-[var(--headings-h3-letter-spacing)] leading-[var(--headings-h3-line-height)] [font-style:var(--headings-h3-font-style)] break-words whitespace-pre-wrap w-full text-center"><RevealCardText text={value} face="front" /></h3>
                : previewRole === 'reveal-back'
                  ? <div className="type-body-m text-foundation-base-darkbase-dark-500 break-words whitespace-pre-wrap w-full text-center"><RevealCardText text={value} face="back" /></div>
                  : previewRole === 'prompt'
                ? <QuestionPrompt text={value} citations={citations} />
                : <QuestionText text={value} inline={inline} citations={citations} className={inline ? undefined : 'text-base'} answerReferences={answerReferences} />
            ) : (
              <p className="text-muted-foreground">Your formatted {label.toLowerCase()} will appear here.</p>
            )}
          </div>
          </AnswerReferenceContext.Provider>
        ) : null}
      </div>
      <p id={`${id}-format-help`} className="text-xs text-muted-foreground">
        {reveal
          ? (inline ? 'Card front supports inline emphasis and text sizes.' : 'Card back supports Markdown paragraphs, headings, lists, quotes, tables and text sizes.')
          : inline
          ? 'Inline formatting: emphasis, links, citations and preset or custom text sizes.'
          : 'Markdown source. Use headings 1–6, lists, quotes and tables. Text size formats a selection; multiple lines become a sized block. Block size formats your selection or the paragraph at the cursor.'}
        {previewRole === 'prompt' ? ' Text defaults to 28px.' : previewRole === 'body' ? ' Subtext defaults to 16px.' : ''}
        {answerReferences ? ' Earlier answer inserts {{question_key}}: learners see their own saved answer to that question. Add |text for an unanswered fallback, as in {{goal|your goal}}.' : ''}
        {' '}Ctrl/Cmd+B for bold; Ctrl/Cmd+I for italic.
        {reveal ? ' Links display as read-only labels. Citation markers stay literal; put sources in the question text.' : ' Add sources in Citations below.'}
      </p>
    </div>
  );
};
